'use strict';

/**
 * PLP In-Grid Content ordering helpers.
 *
 * Runtime contract:
 * - `.plp-grid` is the flex grid container.
 * - direct grid children expose `data-igc-grid-item`.
 * - double IGC cards expose `data-igc-size="double"` and CMS-authored
 *   `data-igc-position`.
 * - `data-igc-base-order` is the runtime DOM-order snapshot used for swaps.
 *
 * The column count must stay aligned with `plp-in-grid-content.css`: 2 columns
 * by default, 3 at 48em, and 4 at 64em.
 */

/** @constant {string} PLP grid container selector. */
const GRID_SELECTOR = '.plp-grid';

/** @constant {string} Any rendered PLP IGC card. */
const IGC_CARD_SELECTOR = '[data-igc-card]';

/** @constant {string} Double IGC cards that may need visual order adjustment. */
const DOUBLE_IGC_SELECTOR =
    '[data-igc-grid-item][data-igc-size="double"][data-igc-position]';

/** @constant {string} Desktop PLP breakpoint; four-column grid. */
const MEDIUM_BREAKPOINT = '(min-width: 64em)';

/** @constant {string} Tablet PLP breakpoint; three-column grid. */
const SMALL_BREAKPOINT = '(min-width: 48em)';

/**
 * @private
 * @param {HTMLElement} grid - `.plp-grid` flex container.
 * @returns {number} Active PLP column count: 2 mobile, 3 tablet, 4 desktop.
 */
function getCurrentColumnCount(grid) {
    const gridViewContainer = grid.closest('[data-grid-view]');

    if (
        gridViewContainer instanceof HTMLElement &&
        gridViewContainer.dataset.gridView === 'large'
    ) {
        return 1;
    }

    if (window.matchMedia(MEDIUM_BREAKPOINT).matches) return 4;

    if (window.matchMedia(SMALL_BREAKPOINT).matches) return 3;

    return 2;
}

/**
 * @private
 * @param {number} position - 1-based CMS-authored grid slot from data-igc-position.
 * @param {number} columnCount - Active PLP column count.
 * @returns {boolean}
 */
function shouldShiftDoubleCard(position, columnCount) {
    return columnCount > 1 && position > 0 && position % columnCount === 0;
}

/**
 * @private
 * @param {HTMLElement} grid - `.plp-grid` flex container.
 * @returns {HTMLElement[]} Direct HTML element children of the grid.
 */
function getGridChildren(grid) {
    return Array.prototype.slice
        .call(grid.children)
        .filter((child) => child instanceof HTMLElement);
}

/**
 * @private
 * @param {HTMLElement[]} children - Current grid children.
 * @param {number} order - Runtime base order, not the authored IGC position.
 * @returns {HTMLElement|null}
 */
function getChildByBaseOrder(children, order) {
    for (let i = 0; i < children.length; i++) {
        if (parseInt(children[i].dataset.igcBaseOrder || '0', 10) === order) {
            return children[i];
        }
    }

    return null;
}

/**
 * Applies visual order for a single PLP grid.
 *
 * First snapshots the current DOM order, then swaps a double IGC card with the
 * next DOM grid item when the authored slot would leave an empty column.
 *
 * @private
 * @param {HTMLElement} grid - `.plp-grid` flex container.
 * @returns {void}
 */
function updateGrid(grid) {
    const children = getGridChildren(grid);
    const columnCount = getCurrentColumnCount(grid);

    for (let i = 0; i < children.length; i++) {
        const order = i + 1;
        // Snapshot DOM order before double-card swaps so later lookups use the
        // server-rendered sequence, not a previous resize calculation.
        children[i].dataset.igcBaseOrder = String(order);
        children[i].style.order = String(order);
    }

    /** @type {HTMLElement[]} */
    const doubleCards = Array.prototype.slice
        .call(grid.querySelectorAll(DOUBLE_IGC_SELECTOR))
        .filter((child) => child instanceof HTMLElement);

    for (let i = 0; i < doubleCards.length; i++) {
        const card = doubleCards[i];
        const position = parseInt(card.dataset.igcPosition || '0', 10);
        const cardBaseOrder = parseInt(card.dataset.igcBaseOrder || '0', 10);

        if (!shouldShiftDoubleCard(position, columnCount)) {
            continue;
        }

        const nextChild = getChildByBaseOrder(children, cardBaseOrder + 1);

        if (nextChild && nextChild !== card) {
            // Move the following item into the open slot and let the double
            // card start the next row for the active breakpoint.
            nextChild.style.order = String(cardBaseOrder);
            card.style.order = String(cardBaseOrder + 1);
        }
    }
}

/**
 * Updates every PLP grid on the page.
 *
 * @private
 * @returns {void}
 */
function updateAllGrids() {
    const grids = document.querySelectorAll(GRID_SELECTOR);

    for (let i = 0; i < grids.length; i++) {
        if (grids[i] instanceof HTMLElement) {
            updateGrid(grids[i]);
        }
    }
}

/**
 * Initializes PLP IGC ordering.
 *
 * Authoring is intentionally capped to the initial PLP grid window
 * (`position <= 24`), so lazy-loaded page-element fragments are product-only.
 * The observer still runs whenever initial IGC exists so appended products are
 * reindexed after infinite scroll; double-card swaps remain double-specific.
 *
 * @returns {void}
 */
export function initPlpInGridContent() {
    if (!document.querySelector(IGC_CARD_SELECTOR)) return;

    updateAllGrids();
    window.addEventListener('resize', updateAllGrids);

    if (window.MutationObserver) {
        const observer = new MutationObserver(updateAllGrids);

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}
